import { Component, OnInit } from '@angular/core';
import { TitleService } from '@app/shared/services/title.service';
import { MenuService } from '@app/shared/services/menu.service';
import { UsersService } from '@app/shared/services/users.service';
import { SessionService } from '@app/shared/services/session.service';
import { ExcelService } from '@app/shared/services/excel.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/internal/operators';
import * as moment from 'moment';

@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss']
})
export class UserListComponent implements OnInit {

    public users: Array<any> = [];
    public maxPages: number = 1;
    public search: string = '';
    public searchChanged: Subject<string> = new Subject<string>();
    public currentPage: number = 1;
    public loadingStatus: boolean = true;
    public noResult: boolean = false;
    public excelLoading: boolean = false;
    public tab: number = 0;
    public filtre: number = 0;

    get filteredUsers(): Array<any> {
        return this.users
            .filter(u => this.tab === 0 ? u.IdAccountDemo === 0 : u.IdAccountDemo > 0)
            .filter(u => {
                if (this.filtre === 1) return u.IdDisabledAccount === 0;
                if (this.filtre === 2) return u.IdDisabledAccount > 0;
                return true;
            });
    }

    changeTab(tab: number): void {
        this.tab = tab;
        this.filtre = 0;
    }

    changeFiltre(filtre: number): void {
        this.filtre = filtre;
    }

    constructor(
        private titleService: TitleService,
        private menu: MenuService,
        private userService: UsersService,
        public session: SessionService,
        private excelService: ExcelService
    ) {
        this.searchChanged
            .pipe(debounceTime(1000), distinctUntilChanged())
            .subscribe(model => {
                this.search = model;
                this.currentPage = 1;
                sessionStorage.setItem('UserFilterSearch', this.search);
                this.updateUsers();
            });
    }

    retrieveFilters() {
        this.search = ('UserFilterSearch' in sessionStorage) ? sessionStorage.getItem('UserFilterSearch') : this.search;
    }

    ngOnInit() {
        this.titleService.setTitle(`Liste des utilisateurs / VOP`);
        this.menu.setMenuState('user', 'user', '');
        this.retrieveFilters();
        if ('UserList' in sessionStorage) {
            this.users = JSON.parse(sessionStorage.getItem('UserList'));
            this.maxPages = parseInt(sessionStorage.getItem('UserListMaxPages'), 10);
            this.currentPage = parseInt(sessionStorage.getItem('UserListCurrentPage'), 10);
            this.loadingStatus = false;
        } else {
            this.updateUsers();
        }
    }

    onSearchChange(search): void {
        this.searchChanged.next(search);
    }

    setUsers(res) {
        this.users = res;
        sessionStorage.setItem('UserList', JSON.stringify(this.users));
        sessionStorage.setItem('UserListMaxPages', this.maxPages.toString());
        sessionStorage.setItem('UserListCurrentPage', this.currentPage.toString());
    }

    updateUsers() {
        this.loadingStatus = true;
        this.currentPage = 1;
        this.userService.updateUserCount(this.search).then(() => {
            this.maxPages = this.userService.maxPages;
        });
        this.userService.updateUsers(this.currentPage, this.search).then((res) => {
            this.loadingStatus = false;
            if (this.userService.users.length === 0) {
                this.noResult = true;
            } else {
                this.noResult = false;
                this.setUsers(res);
            }
        });
    }

    nextPage(): any {
        this.loadingStatus = true;
        this.userService.updateUserCount(this.search).then(() => {
            this.maxPages = this.userService.maxPages;
            if (this.currentPage < this.userService.maxPages) {
                this.currentPage += 1;
                this.userService.updateUsers(this.currentPage, this.search).then((res) => {
                    this.loadingStatus = false;
                    if (this.userService.users.length === 0) {
                        this.noResult = true;
                    } else {
                        this.noResult = false;
                        this.setUsers(res);
                    }
                });
            }
        });
    }

    goPage(page): any {
        this.loadingStatus = true;
        this.userService.updateUserCount(this.search).then(() => {
            this.maxPages = this.userService.maxPages;
            this.currentPage = page;
            this.userService.updateUsers(this.currentPage, this.search).then((res) => {
                this.loadingStatus = false;
                if (this.userService.users.length === 0) {
                    this.noResult = true;
                } else {
                    this.noResult = false;
                    this.setUsers(res);
                }
            });
        });
    }

    prevPage(): any {
        this.loadingStatus = true;
        this.userService.updateUserCount(this.search).then(() => {
            this.maxPages = this.userService.maxPages;
            if (this.currentPage > 1) {
                this.currentPage -= 1;
                this.userService.updateUsers(this.currentPage, this.search).then((res) => {
                    this.loadingStatus = false;
                    if (this.userService.users.length === 0) {
                        this.noResult = true;
                    } else {
                        this.noResult = false;
                        this.setUsers(res);
                    }
                });
            }
        });
    }

    exportDataAsXLSX(): any {
        let datas = [];
        let cols = this.excelService.initCols(["Nom", "Adresse", "Ville", "Code postal", "E-mail",
            "Téléphone", "Code adhérent", "Date de création du compte", "Compte activé"]);

        this.excelLoading = true;
        this.userService.updateUserCount().then(async () => {
            this.maxPages = Math.max(this.userService.maxPages, 1);
            for (let i = 1; i <= this.maxPages; i++) {
                let results = await this.userService.updateUsers(i, '');
                results.forEach((result) => {
                    let data = {
                        "Nom": result.FirstName + ' ' + result.LastName,
                        "E-mail": result.EMailAddress,
                        "Téléphone": result.Mobile,
                        "Code adhérent": result.CodeSociety,
                        "Date de création du compte": (result.ISODateCreation.length !== 0) ? moment(result.ISODateCreation).format('DD/MM/YYYY').split('T')[0] : '',
                        "Adresse": result.Address,
                        "Ville": result.City,
                        "Code postal": result.PostCode,
                        "Compte activé": result.IdDisabledAccount > 0 ? 'Désactivé' : 'Activé'
                    };

                    Object.values(data).forEach((value, index) => {
                        if (typeof value === 'string') {
                            cols[index].wch = Math.max(value.length, cols[index].wch);
                        }
                    });
                    datas.push(data);
                });
            }
            cols = this.excelService.extendCols(cols);
            this.excelService.exportExcel(datas, "vop-users", cols);
            this.excelLoading = false;
        });
    }

}
